import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  TextField,
  Checkbox,
  FormControlLabel,
  Alert,
} from "@mui/material";

import axios from "axios";

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL || process.env.REACT_APP_API_URL;

const pad2 = (n) => String(n).padStart(2, "0");

// MM/DD/YYYY
const fmtDate = (val) => {
  if (!val) return "—";
  const d = new Date(val);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
};

// MM/DD/YYYY hh:mm AM
const fmtUsDateTimeStrict = (val) => {
  if (!val) return "—";
  const d = new Date(val);
  if (isNaN(d.getTime())) return "—";
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const yyyy = d.getFullYear();
  let h = d.getHours();
  const AMPM = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  const hh = pad2(h);
  const min = pad2(d.getMinutes());
  return `${mm}/${dd}/${yyyy} ${hh}:${min} ${AMPM}`;
};

// Extract "Name, username" from a message like "Digitally signed by Name, user on ..."
const extractSignedBy = (msg) => {
  if (!msg) return "";
  const m = String(msg).match(/digitally\s*signed\s*by\s*(.+?)\s*on\b/i);
  return m?.[1]?.trim() || "";
};

const ARMHSDischargeSummaryPreview = ({ patientFormId, data }) => {
  const [note, setNote] = useState(null);       // Discharge Summary
  const [header, setHeader] = useState(null);   // Patient Form Header
  const [clientSigSrc, setClientSigSrc] = useState(""); // resolved client signature image URL
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const resolvedPatientFormId = useMemo(
    () =>
      patientFormId ??
      data?.patientFormId ??
      data?.patientformID ??
      data?.id ??
      data?.Id ??
      null,
    [patientFormId, data]
  );

  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    const run = async () => {
      if (!resolvedPatientFormId) {
        setError("Missing patientFormId");
        setLoading(false);
        return;
      }
      if (!API_BASE_URL) {
        setError("API base URL is not set.");
        setLoading(false);
        return;
      }
      const token = localStorage.getItem('neuro_patientportal_auth_token')
      if (!token) {
        setError("Not authenticated (missing token)");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const headers = { Authorization: `Bearer ${token}` };

        const [dsRes, headerRes] = await Promise.all([
          axios.get(
            `${API_BASE_URL}/Rehabilitative/getDischargeSummaryPatientFormId`,
            {
              params: { patientformID: resolvedPatientFormId },
              headers,
              signal: controller.signal,
              validateStatus: (s) =>
                (s >= 200 && s < 300) || s === 204 || s === 404,
            }
          ),
          axios.get(`${API_BASE_URL}/Forms/GetPatientFormsbyId`, {
            params: { Id: resolvedPatientFormId },
            headers,
            signal: controller.signal,
            validateStatus: (s) =>
              (s >= 200 && s < 300) || s === 204 || s === 404,
          }),
        ]);

        if (!active) return;

        const dsNote = dsRes.status === 204 || dsRes.status === 404 ? null : dsRes.data;
        const hdr = headerRes.status === 204 || headerRes.status === 404 ? null : headerRes.data;

        setNote(dsNote);
        setHeader(hdr);

        // Resolve client signature image: try direct URL first, otherwise fetch a view link
        const clientSigRaw =
          dsNote?.clientsURL ||
          hdr?.clientsURL ||
          dsNote?.clientSignatureURL ||
          dsNote?.patientSignatureURL ||
          "";

        if (clientSigRaw) {
          if (/^(https?:|data:|blob:)/i.test(clientSigRaw)) {
            if (active) setClientSigSrc(clientSigRaw);
          } else {
            try {
              const res = await fetch(
                `${API_BASE_URL}/PatientDocument/ViewPatientDocument?path=${encodeURIComponent(clientSigRaw)}`,
                {
                  headers: { Authorization: `Bearer ${token}` },
                  signal: controller.signal,
                }
              );
              if (res.ok) {
                const link = await res.text();
                if (active && link) setClientSigSrc(link);
              }
            } catch { }
          }
        } else {
          setClientSigSrc("");
        }

        if (!dsNote && !hdr) {
          setError("No Discharge Summary found for this form.");
        }
      } catch (err) {
        if (!active) return;
        console.error("Discharge Summary fetch error:", err);
        setError(
          err?.response?.data?.message ||
          err?.message ||
          "Failed to fetch Discharge Summary."
        );
        setNote(null);
        setHeader(null);
      } finally {
        if (active) setLoading(false);
      }
    };

    run();
    return () => {
      active = false;
      controller.abort();
    };
  }, [resolvedPatientFormId]);

  if (loading)
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );

  if (error)
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">{error}</Alert>
      </Box>
    );

  if (!note && !header)
    return (
      <Typography align="center" color="text.secondary" sx={{ mt: 4 }}>
        No data available.
      </Typography>
    );

  // HEADER FIELDS (simple, direct)
  const headerTitle =
    header?.customFormTitle || note?.customFormTitle || "ARMHS Discharge Summary";
  const dos = note?.assessmentDate || header?.assessmentDate || header?.createdDate || null;

  const fileNo =
    header?.fileNo ||
    header?.fileno ||
    header?.accountNo ||
    note?.fileno ||
    note?.fileNo ||
    note?.accountNo ||
    "—";
  const clientName = header?.patientName || note?.patientName || "—";
  const createdBy = note?.createdBy || header?.createdBy || "—";

  // FORM FIELDS (simple, direct)
  const titleField = note?.title || header?.customFormTitle || headerTitle;

  // SIGNATURES
  // Client: date in US format
  const clientSigDate =
    note?.patientSignatureDATE ||
    header?.patientSignatureDATE ||
    note?.clientSignatureDATE ||
    header?.clientSignatureDATE ||
    null;

  // Staff (make it match ARMHS "Name, username")
  const staffDateTime =
    note?.providerDate ||
    note?.professionalDate ||
    note?.staffsignatureDATE ||
    note?.staffSignatureDATE ||
    header?.providerDate ||
    header?.professionalDate ||
    header?.staffsignatureDATE ||
    null;

  // Prefer a staff message that already contains "Digitally signed by Name, username ..."
  const staffMsgCandidates = [
    note?.message,
    header?.message,
    note?.massagestaffSignature,
    header?.massagestaffSignature,
    note?.staffSignature,
    header?.staffSignature,
  ].filter(Boolean);

  const staffMessagePreferred =
    staffMsgCandidates.find((m) => /digitally\s*signed\s*by/i.test(String(m))) ||
    staffMsgCandidates[0] ||
    "";

  // Try to pull "Name, username" from the staff message; if not, reuse director's "Name, username" if available
  const staffSignedNameFromMsg = extractSignedBy(staffMessagePreferred);
  const directorSignedNameFromMsg = extractSignedBy(
    note?.messagetreatmentdirector || header?.messagetreatmentdirector || ""
  );

  // Fall back only to real name fields (avoid usernames like "Awais09")
  const staffNameFallback =
    note?.mhPractitioner ||
    header?.mhPractitioner ||
    note?.practitionerName ||
    header?.practitionerName ||
    note?.professionalName ||
    header?.professionalName ||
    note?.staffSignatureName ||
    header?.staffSignatureName ||
    note?.staffName ||
    header?.staffName ||
    "";

  const staffNameForDisplay =
    staffSignedNameFromMsg || directorSignedNameFromMsg || staffNameFallback;

  const staffDisplay = (() => {
    const msg = String(staffMessagePreferred || "");
    if (/digitally\s*signed\s*by/i.test(msg)) return msg; // already "Name, username"
    const dt = fmtUsDateTimeStrict(staffDateTime);
    const hasDate = dt !== "—";
    const nm = (staffNameForDisplay || "").trim();
    if (nm && hasDate) return `Digitally signed by ${nm} on ${dt}.`;
    if (nm) return `Digitally signed by ${nm}.`;
    if (hasDate) return `Digitally signed on ${dt}.`;
    return msg || "—";
  })();

  // Director (kept the same)
  const directorMessage =
    note?.messagetreatmentdirector || header?.messagetreatmentdirector || "";

  return (
    <Paper sx={{ p: 0, mb: 4, borderRadius: 2, boxShadow: "0 3px 8px rgba(0,0,0,0.08)" }}>
      {/* STICKY HEADER */}
      <Box
        sx={{
          position: "sticky",
          top: "-16px",
          zIndex: (t) => t.zIndex.appBar,
          bgcolor: "#fff",
          p: 4,
          pb: 0,
          mb: 3,
        }}
      >
        <Typography variant="subtitle2" sx={{ position: "absolute", top: 16, left: 16 }}>
          <strong>DOS:</strong> {fmtDate(dos)}
        </Typography>

        <Typography
          variant="h6"
          sx={{
            position: "absolute",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            fontWeight: "bold",
          }}
        >
          {headerTitle}
        </Typography>

        <Box sx={{ height: 28 }} />
        <Divider />
      </Box>

      {/* PATIENT STRIP */}
      <Box sx={{ px: 4 }}>
        <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <Typography variant="body2">
              <strong>File #:</strong> {fileNo}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} textAlign="center">
            <Typography variant="body2">
              <strong>Client:</strong> {clientName}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} textAlign="right">
            <Typography variant="body2">
              <strong>Created By:</strong> {createdBy}
            </Typography>
          </Grid>
        </Grid>
        <Divider sx={{ mb: 3 }} />
      </Box>

      {/* CONTENT (unchanged) */}
      <Box sx={{ px: 4, pb: 4 }}>
        {/* Title + Assessment Date */}
        <Grid container spacing={2} sx={{ mb: 2, alignItems: "center" }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth={false}
              label="Title"
              value={titleField}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiInputBase-root": {
                  height: 40,
                  fontSize: "0.900rem",
                  padding: "0 10px",
                  width: "350px",
                },
                "& .MuiInputLabel-root": { fontSize: "0.875rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth={false}
              label="Assessment Date"
              value={fmtDate(note?.assessmentDate || header?.assessmentDate)}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiInputBase-root": {
                  height: 40,
                  fontSize: "0.900rem",
                  padding: "0 10px",
                  width: "350px",
                },
                "& .MuiInputLabel-root": { fontSize: "0.875rem" },
              }}
            />
          </Grid>
        </Grid>

        {/* Document Status */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            { label: "Printed and Filed", value: note?.isPrinted ?? header?.isPrinted },
            { label: "Inactive", value: note?.inactive ?? header?.inactive },
            { label: "Audit Ready", value: note?.isAuditReady ?? header?.isAuditReady },
            { label: "Lock", value: note?.isChecked ?? header?.isChecked },
          ].map((item, idx) => (
            <Grid item key={idx}>
              <FormControlLabel control={<Checkbox checked={!!item.value} disabled />} label={item.label} />
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Basic Info */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Name"
              value={clientName || ""}
              fullWidth
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Date of Birth"
              value={fmtDate(note?.dOB || header?.dob || header?.dOB)}
              fullWidth
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="File No"
              value={fileNo || ""}
              fullWidth
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        {/* Referral Info */}
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Referred by:
        </Typography>
        <Grid container spacing={2}>
          {[
            { label: "Parent", value: note?.isParent },
            { label: "Court Ordered", value: note?.isCourt },
            { label: "Mental Health Professional", value: note?.isMental },
            { label: "County Social Worker / Probation Officer", value: note?.isCounty },
            { label: "Other", value: note?.isOther },
          ].map((item, idx) => (
            <Grid item xs={12} sm={6} md={3} key={idx}>
              <FormControlLabel control={<Checkbox checked={!!note?.[item.key]} disabled />} label={item.label} />
            </Grid>
          ))}
        </Grid>
        {note?.otherDetail && (
          <TextField
            label="Other Detail"
            value={note.otherDetail}
            fullWidth
            multiline
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            sx={{ my: 2 }}
          />
        )}

        <Divider sx={{ my: 2 }} />

        {/* ARMHS Services Provided */}
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          ARMHS Services Provided:
        </Typography>

        <Grid container columnSpacing={6} rowSpacing={2} alignItems="flex-start">
          <Grid item xs={12} md={6}>
            <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
              Psychotherapy
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <FormControlLabel control={<Checkbox checked={!!note?.isindividual} disabled />} label="Individual" />
              <FormControlLabel control={<Checkbox checked={!!note?.isfamily} disabled />} label="Family" />
              <FormControlLabel control={<Checkbox checked={!!note?.isgroup} disabled />} label="Group" />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
              Skills
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <FormControlLabel control={<Checkbox checked={!!note?.isSkillsindividual} disabled />} label="Individual" />
              <FormControlLabel control={<Checkbox checked={!!note?.isSkillsfamily} disabled />} label="Family" />
              <FormControlLabel control={<Checkbox checked={!!note?.isSkillsgroup} disabled />} label="Group" />
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Diagnosis Section */}
        <Typography variant="h6" sx={{ mt: 2, mb: 1.5 }}>
          1. Client Problem(s) and Diagnosis
        </Typography>

        <TextField
          label="Client Problem(s)"
          value={note?.clientsProblems || ""}
          fullWidth
          multiline
          InputProps={{ readOnly: true }}
          InputLabelProps={{ shrink: true }}
          sx={{ my: 2 }}
        />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          DSM 5 Diagnosis
        </Typography>
        {note?.dsM5Diagnosislist?.map((d, i) => (
          <Box key={i} sx={{ pl: 2, mt: i === 0 ? 0.5 : 0, mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="DSM 5"
                  value={d?.dSM5 || ""}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="ICD 10"
                  value={d?.iCD10 || ""}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
            <TextField
              label="Specifier"
              value={d?.specifier || ""}
              fullWidth
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={{ mt: 1 }}
            />
          </Box>
        ))}

        <Divider sx={{ my: 3 }} />

        {/* Treatment Summary */}
        <Typography variant="h6">2. Treatment Summary (methods, duration, visits)</Typography>
        <Grid container spacing={2} sx={{ my: 2 }}>
          <Grid item xs={12} md={3}>
            <TextField
              label="Total Sessions"
              value={note?.totalSessions || ""}
              fullWidth
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Individual Sessions"
              value={note?.individualSessions || ""}
              fullWidth
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Family Sessions"
              value={note?.familySessions || ""}
              fullWidth
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Group Sessions"
              value={note?.groupSessions || ""}
              fullWidth
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        <TextField
          label="Summary"
          value={note?.summary || ""}
          fullWidth
          multiline
          InputProps={{ readOnly: true }}
          InputLabelProps={{ shrink: true }}
        />

        <Divider sx={{ my: 3 }} />

        {/* Outcome */}
        <Typography variant="h6">3. Outcome (Current condition)</Typography>
        <TextField
          value={note?.outComes || ""}
          fullWidth
          multiline
          InputProps={{ readOnly: true }}
          InputLabelProps={{ shrink: true }}
          sx={{ my: 1 }}
        />

        {/* Reason for Termination */}
        <Typography variant="h6">4. Reason For Termination</Typography>
        {[
          { label: "Client Discontinued", key: "isPatientDiscontinued" },
          { label: "Transferred", key: "isTransferOther" },
          { label: "Treatment goals met", key: "isTreatmentMet" },
          { label: "Relocated", key: "isRelocated" },
          { label: "Other", key: "other" },
        ].map((item, idx) => (
          <FormControlLabel key={idx} control={<Checkbox checked={!!note?.[item.key]} disabled />} label={item.label} />
        ))}
        {note?.explainOther && (
          <TextField
            label="Explain"
            value={note.explainOther}
            fullWidth
            multiline
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            sx={{ my: 2 }}
          />
        )}

        {/* Recommendations */}
        <Typography variant="h6" sx={{ mt: 3 }}>
          5. Treatment Recommendations/Referrals
        </Typography>
        <TextField
          value={note?.refferals || ""}
          fullWidth
          multiline
          InputProps={{ readOnly: true }}
          InputLabelProps={{ shrink: true }}
          sx={{ my: 1 }}
        />

        <Divider sx={{ my: 3 }} />

        {/* Signatures */}
        <Typography variant="h6" sx={{ mb: 1 }}>
          Signatures
        </Typography>

        {/* Client: same line layout kept */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 1 }}>
          <Typography variant="subtitle2" sx={{ minWidth: 220 }}>
            <strong>Client Signature:</strong>
          </Typography>
          {clientSigSrc ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <img
                src={clientSigSrc}
                alt="Client signature"
                style={{
                  width: 200,
                  height: 80,
                  border: "2px solid #000",
                  background: "#f5f5f5",
                  padding: 10,
                  borderRadius: 8,
                  objectFit: "contain",
                }}
              />
              {clientSigDate ? (
                <Typography sx={{ fontWeight: 600 }}>
                  Signed on {fmtUsDateTimeStrict(clientSigDate)}
                </Typography>
              ) : null}
            </Box>
          ) : (
            <Typography sx={{ fontWeight: 600 }}>
              {clientSigDate ? `Signed on ${fmtUsDateTimeStrict(clientSigDate)}` : "—"}
            </Typography>
          )}
        </Box>

        {/* Staff: now matches ARMHS style on the same line */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 1 }}>
          <Typography variant="subtitle2" sx={{ minWidth: 220 }}>
            <strong>Staff Signature:</strong>
          </Typography>
          <Typography sx={{ fontWeight: 600 }}>
            {staffDisplay}
          </Typography>
        </Box>

        {/* ARMHS Treatment Director: unchanged */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Typography variant="subtitle2" sx={{ minWidth: 220 }}>
            <strong>ARMHS Treatment Director:</strong>
          </Typography>
          <Typography sx={{ fontWeight: 600 }}>
            {directorMessage || "—"}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default ARMHSDischargeSummaryPreview;